import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';

import { ApiAuthorizationType, ApiDef, ApiParameter } from '@blaze-shared/api-contracts-domain';
import { ConnectorConfiguration, FormValues } from '@blaze-shared/connectors-domain';
import { isEmptyish, isNullish, toFloat } from '@blaze-shared/shared-util';

import {
  API_BUILDER_PARAMETERS_PATTERN,
  ApiActionStatus,
  ApiBuilderAction,
  ApiBuilderCommonConfig,
  ApiBuilderParameterExtended,
  ApiBuilderTestParameterDefinition,
  DEFAULT_API_BUILDER_ACTION,
  OptionalParameterDefaultValue,
  ParameterDataType,
  ParameterPaginationMethod,
  ParameterType,
  SerializedOptionalParameterDefaultValue,
} from '../api-builder.model';
import { toParameterDataType } from '../utils/api-builder-utils';

@Injectable({
  providedIn: 'root',
})
export class ApiBuilderService {
  private newActionId = 0;
  private readonly connectorUpdated$$ = new Subject<ConnectorConfiguration>();
  private readonly simpleParamDetectRegExp = new RegExp(
    `^\\\${(?<type>${API_BUILDER_PARAMETERS_PATTERN})\\((?<name>[\\w-]+)\\s*(,\\s*(?<param2>[\\w-. ]*))?\\s*(,\\s*(?<param3>[\\w-]*))?\\)}$`,
  );
  private readonly allParamsDetectRegExp = new RegExp(
    `\\\${(?<type>${API_BUILDER_PARAMETERS_PATTERN})\\((?<name>[\\w-]+)\\s*(,\\s*(?<param2>[\\w-. ]*))?\\s*(,\\s*(?<param3>[\\w-]*))?\\)}`,
    'g',
  );
  private readonly urlParameterRegExp = new RegExp('{([\\w-]+?)}', 'gm');

  createNewAction(): ApiBuilderAction {
    this.newActionId += 1;
    return { ...DEFAULT_API_BUILDER_ACTION, id: this.newActionId };
  }

  convertParameterToExtended(param: ApiParameter): ApiBuilderParameterExtended | undefined {
    const match = this.simpleParamDetectRegExp.exec(param.value);
    const { type, name = param.value, param2, param3 } = match?.groups || {};

    if (!param.name && !param.value) {
      return undefined;
    }
    if (type === 'Parameter') {
      return {
        name: param.name,
        value: name,
        type: ParameterType.Dynamic,
        dataType: toParameterDataType(param2),
      };
    }
    if (type === 'ParameterOptional') {
      const dataType = toParameterDataType(param3);
      return {
        name: param.name,
        value: name,
        type: ParameterType.Dynamic,
        optional: true,
        dataType,
        defaultValue: dataType
          ? this.deserializeParameterDefaultValue(param2, dataType)
          : undefined,
      };
    }
    if (type === 'Pagination') {
      const processed: ApiBuilderParameterExtended = {
        name: param.name,
        value: name,
        type: ParameterType.Pagination,
      };
      if (name === 'pageNumber') {
        processed.paginationMethod = ParameterPaginationMethod.PageNumber;
      } else if (name === 'maxCount') {
        processed.paginationMethod = ParameterPaginationMethod.PageSize;
      } else if (name === 'offset') {
        processed.paginationMethod = ParameterPaginationMethod.RowOffset;
      } else {
        processed.type = ParameterType.FixedValue;
        processed.value = param.value;
      }
      return processed;
    }

    return {
      ...param,
      type: ParameterType.FixedValue,
    };
  }

  convertParametersToExtended(params: ApiParameter[]): ApiBuilderParameterExtended[] {
    return params
      .map((p) => this.convertParameterToExtended(p))
      .filter((p): p is ApiBuilderParameterExtended => !!p);
  }

  convertExtendedToParameter(param: ApiBuilderParameterExtended): ApiParameter | undefined {
    if (!param.name && !param.value) {
      return undefined;
    }

    if (param.type === ParameterType.Dynamic && !param.optional) {
      const paramArgs = [param.name];

      if (param.dataType) {
        paramArgs.push(param.dataType);
      }

      return {
        name: param.name,
        value: `\${Parameter(${paramArgs.join(',')})}`,
      };
    }

    if (param.type === ParameterType.Dynamic && param.optional) {
      const paramArgs = [param.name];

      if (param.dataType) {
        paramArgs.push(this.serializeParameterDefaultValue(param.defaultValue));
        paramArgs.push(param.dataType);
      }

      return {
        name: param.name,
        value: `\${ParameterOptional(${paramArgs.join(',')})}`,
      };
    }

    if (param.type === ParameterType.Pagination && param.paginationMethod) {
      return {
        name: param.name,
        value: `\${Pagination(${param.paginationMethod})}`,
      };
    }

    return {
      name: param.name,
      value: param.value,
    };
  }

  convertExtendedToParameters(params: ApiBuilderParameterExtended[]): ApiParameter[] {
    return params
      .map((p) => this.convertExtendedToParameter(p))
      .filter((p): p is ApiParameter => !!p);
  }

  getAllParametersForTest(
    common: ApiBuilderCommonConfig,
    action: ApiBuilderAction,
  ): ApiBuilderTestParameterDefinition[] {
    const {
      data: { url, headers, urlParameters },
    } = action;
    const parameters: ApiBuilderTestParameterDefinition[] = [];

    for (const match of url.matchAll(this.urlParameterRegExp)) {
      const name = match[1];
      parameters.push({
        name,
        optional: false,
        value: (name && action.parameters?.[name]) || '',
      });
    }

    const bodyParameters = action.data.bodyParameter
      ? [
          {
            name: action.data.bodyParameter.name,
            type: ParameterType.Dynamic,
            optional: true,
            dataType: 'string' as const,
            defaultValue: '',
          },
        ]
      : this.getParametersFromActionBody(action.data.body);

    [headers, urlParameters, bodyParameters]
      .flat()
      .forEach(({ name, type, optional, dataType, defaultValue }) => {
        if (name && type === ParameterType.Dynamic) {
          parameters.push({
            name,
            optional: !!optional,
            value: action.parameters?.[name] || '',
            dataType,
            defaultValue,
          });
        }
      });

    return parameters.filter(
      (param, index, array) => array.findIndex((p) => param.name === p.name) === index,
    );
  }

  getParametersFromActionBody(
    body: any[] | Record<string, any> | null,
  ): ApiBuilderParameterExtended[] {
    const parameters: ApiBuilderParameterExtended[] = [];

    const walkOverProperties = (data: any): void => {
      if (data && typeof data === 'object') {
        Object.values(data).forEach((v) => walkOverProperties(v));
      }
      if (typeof data === 'string' && this.simpleParamDetectRegExp.test(data)) {
        const parameter = this.convertParameterToExtended({ name: '', value: data });
        if (parameter) {
          parameters.push({ ...parameter, name: parameter.value });
        }
      }
    };

    walkOverProperties(body);

    return parameters;
  }

  mapStoreToDefinition(common: ApiBuilderCommonConfig, actions: ApiBuilderAction[]): ApiDef {
    const { apiAuthorizationType } = common;

    return {
      baseUrl: common.baseUrl,
      headers: common.headers.filter((p) => !isEmptyish(p)),
      urlParameters: [],
      apiAuthorizationType: common.apiAuthorizationType,
      basicAuthorization:
        apiAuthorizationType === ApiAuthorizationType.Basic ? common.basicAuthorization : null,
      bearerTokenAuthorization:
        apiAuthorizationType === ApiAuthorizationType.Bearer
          ? common.bearerTokenAuthorization
          : null,
      oAuth2Authorization:
        apiAuthorizationType === ApiAuthorizationType.OAuth2 ? common.oAuth2Authorization : null,
      version: 2,
      actions: actions.map(({ data, resultPath, parameters }) => {
        return {
          body: data.body,
          bodyParameter: data.bodyParameter,
          errorResultPath: data.errorResultPath,
          headers: this.convertExtendedToParameters(data.headers),
          httpMethod: data.httpMethod,
          isTested: !!data.isTested,
          name: data.name,
          noContent: data.noContent,
          parameters,
          resultColumns: data.resultColumns,
          resultPath,
          successStatusCode: Number.parseInt(data.successStatusCode, 10),
          url: this.serializeParameterInUrl(data.url),
          urlParameters: this.convertExtendedToParameters(data.urlParameters),
          useCookies: data.useCookies ?? true,
        };
      }),
    };
  }

  mapApiDefinitionToStore(apiDefinition?: ApiDef): {
    common: ApiBuilderCommonConfig;
    actions: ApiBuilderAction[];
  } {
    const common: ApiBuilderCommonConfig = {
      baseUrl: apiDefinition?.baseUrl || '',
      headers: apiDefinition?.headers || [],
      apiAuthorizationType: apiDefinition?.apiAuthorizationType || ApiAuthorizationType.NoAuth,
      basicAuthorization: apiDefinition?.basicAuthorization || undefined,
      bearerTokenAuthorization: apiDefinition?.bearerTokenAuthorization || undefined,
      oAuth2Authorization: apiDefinition?.oAuth2Authorization || undefined,
    };

    const actions: ApiBuilderAction[] =
      apiDefinition?.actions.map((action) => {
        this.newActionId += 1;
        return {
          id: this.newActionId,
          status: ApiActionStatus.PRISTINE,
          expanded: true,
          resultPath: action.resultPath,
          parameters: action.parameters,
          response: '',
          data: {
            body: action.body,
            bodyParameter: action.bodyParameter,
            bodyParameters: this.getParametersFromActionBody(action.body),
            errorResultPath: action.errorResultPath ?? '',
            headers: this.convertParametersToExtended(action.headers),
            httpMethod: action.httpMethod,
            isTested: action.isTested,
            name: action.name,
            noContent: action.noContent,
            resultColumns: action.resultColumns,
            successStatusCode: String(action.successStatusCode),
            url: this.deserializeParametersInUrl(action.url),
            urlParameters: this.convertParametersToExtended(action.urlParameters),
            useCookies: isNullish(action.useCookies) ? true : action.useCookies,
          },
        };
      }) || [];

    return {
      common,
      actions,
    };
  }

  notifyConnectorUpdated(connector: ConnectorConfiguration): void {
    this.connectorUpdated$$.next(connector);
  }

  observeConnectorUpdated(): Observable<ConnectorConfiguration<FormValues>> {
    return this.connectorUpdated$$.asObservable();
  }

  serializeParameterInUrl(url: string): string {
    return url.replace(this.urlParameterRegExp, (match, name) => {
      const serialized = this.convertExtendedToParameter({
        name,
        value: '',
        type: ParameterType.Dynamic,
      });
      return serialized?.value || '';
    });
  }

  deserializeParametersInUrl(url: string): string {
    return url.replace(this.allParamsDetectRegExp, (match, type, name) =>
      name ? `{${name}}` : '',
    );
  }

  serializeParameterDefaultValue(
    value: OptionalParameterDefaultValue,
  ): SerializedOptionalParameterDefaultValue {
    switch (value) {
      case undefined:
        return '';
      case true:
        return 'true';
      case false:
        return 'false';
      default:
        return Array.isArray(value)
          ? value.map((v) => JSON.stringify(v)).join(',')
          : value.toString();
    }
  }

  deserializeParameterDefaultValue(
    value: SerializedOptionalParameterDefaultValue,
    dataType: ParameterDataType,
  ): OptionalParameterDefaultValue {
    if (dataType === 'boolean') {
      switch (value) {
        case 'true':
          return true;
        case 'false':
          return false;
        default:
          return undefined;
      }
    }

    if (dataType === 'number' && value.length) {
      return toFloat(value, 0);
    }

    if (dataType === 'string') {
      return value.trim();
    }

    if (dataType === 'array') {
      const compatValue = /^\[.*\]$/.exec(value) ? value.slice(1, -1) : value;
      if (!compatValue) {
        return [];
      }
      const segments = compatValue.split(',');
      return segments.map((segment) => {
        try {
          return JSON.parse(segment);
        } catch {
          return segment;
        }
      });
    }

    return value;
  }
}
